import React from "react";
import { Compass, ArrowDown } from "lucide-react";

/**
 * ConciergeReachMeter — one-line summary banner pinned to the top of the
 * concierge dashboard, just above the Brief Marketplace.
 *
 * Reads the three service-scope flags directly off the provider doc and
 * narrates the current reach in plain language. No fake match estimates
 * or counts — only honest, factual guidance about what each toggle does
 * and how to expand reach by enabling the missing ones.
 *
 * States:
 *  • 0 enabled → "Your reach isn't set yet…" + nudge to scroll to editor
 *  • 1–2 enabled → "Your reach covers {list}. Add {missing} to broaden the
 *    requests you receive."
 *  • 3 enabled → "Your reach covers Local Support, Remote Planning, and
 *    Travel Coordination — every kind of brief can match you."
 */
const ALL = [
  { key: "serves_locally",        label: "Local Support" },
  { key: "remote_available",      label: "Remote Planning" },
  { key: "travel_coordination",   label: "Travel Coordination" },
];

function formatList(items) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

export default function ConciergeReachMeter({ provider }) {
  if (!provider) return null;

  // Defaults mirror the public-profile rendering: Local + Remote default ON;
  // Travel defaults OFF unless explicitly enabled.
  const state = {
    serves_locally: provider.serves_locally !== false,
    remote_available: provider.remote_available !== false,
    travel_coordination: Boolean(provider.travel_coordination),
  };
  const enabled = ALL.filter((f) => state[f.key]);
  const missing = ALL.filter((f) => !state[f.key]);
  const onCount = enabled.length;

  let title;
  let body;
  if (onCount === 0) {
    title = "Define your Concierge Reach.";
    body = "Choose the ways you help — Local Support, Remote Planning, or Travel Coordination — so the right briefs find you.";
  } else if (onCount === 3) {
    title = "Your reach is fully open.";
    body = "You support Local, Remote, and Travel requests — every matched brief can reach you.";
  } else {
    title = `Your reach covers ${formatList(enabled.map((e) => e.label))}.`;
    body = `Enable ${formatList(missing.map((m) => m.label))} to broaden the requests you receive.`;
  }

  const scrollToEditor = () => {
    const el = document.querySelector('[data-testid="service-scope-widget"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="concierge-reach-meter"
      data-reach-count={onCount}
      className="mt-6 rounded-2xl border border-[#C9A45C]/30 bg-[#FFF8EE]/70 backdrop-blur-sm px-5 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-4 shadow-[0_8px_22px_-16px_rgba(7,26,45,0.18)]"
    >
      <div className="flex items-center gap-3 shrink-0">
        <div className="w-11 h-11 rounded-full bg-white border border-[#C9A45C]/40 flex items-center justify-center text-[#064E3B] shadow-[0_4px_10px_-6px_rgba(6,78,59,0.25)]">
          <Compass size={18} strokeWidth={1.8} />
        </div>
        <div className="hidden sm:block">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#C9A45C]">Concierge Reach</div>
          <div className="text-[12px] text-[#071A2D]/55" data-testid="reach-tally">
            {onCount} of 3 enabled
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div data-testid="reach-title" className="text-[#071A2D] font-serif text-[18px] sm:text-[20px] leading-snug">
          {title}
        </div>
        <div data-testid="reach-body" className="text-[13px] sm:text-[13.5px] text-[#071A2D]/70 mt-1 leading-relaxed">
          {body}
        </div>
      </div>

      {/* Always visible scroll-to-editor CTA — quiet, premium, no numbers */}
      <button
        type="button"
        onClick={scrollToEditor}
        data-testid="reach-cta"
        className="self-start sm:self-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#064E3B] text-white hover:brightness-110 px-4 py-2 text-[12.5px] font-medium shadow-[0_8px_18px_-10px_rgba(6,78,59,0.6)] transition"
      >
        {onCount === 3 ? "Review" : "Adjust reach"}
        <ArrowDown size={13} />
      </button>
    </section>
  );
}
